(() => {
  'use strict';

  const GLOBAL_KEY = 'AVReviewsStorefrontV3';
  const VERSION = '3.1.0';
  const API_ORIGIN = 'https://storefront-api.loox.io';
  const ROOT_SELECTOR = '[data-av-reviews-root]';
  const GA_EVENT = Object.freeze({
    view: 'australia:reviews_section_viewed',
    advance: 'australia:reviews_card_advanced',
    expand: 'australia:reviews_card_expanded',
    product: 'australia:reviews_product_clicked',
    all: 'australia:reviews_all_clicked',
    error: 'australia:reviews_error'
  });

  const existing = window[GLOBAL_KEY];
  if (existing && typeof existing.scan === 'function') {
    existing.scan();
    return;
  }

  const state = {
    version: VERSION,
    scan
  };
  window[GLOBAL_KEY] = state;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function make(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  }

  function safeNumber(value, fallback = 0) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function unique(values) {
    return [...new Set(values.filter(Boolean).map(String))];
  }

  function parseContext(root) {
    const node = root.querySelector('[data-av-review-product-scope]');
    if (!node) return { categoryProducts: [] };
    try {
      const parsed = JSON.parse(node.textContent || '{}');
      parsed.categoryProducts = Array.isArray(parsed.categoryProducts) ? parsed.categoryProducts : [];
      return parsed;
    } catch (_) {
      return { categoryProducts: [] };
    }
  }

  function publish(root, name, payload = {}) {
    const safePayload = {
      surface: root.dataset.avReviewSurface || 'unknown',
      scope: root.dataset.avReviewScope || 'unknown',
      ...payload
    };
    try {
      if (window.AVAnalytics && typeof window.AVAnalytics.custom === 'function') {
        window.AVAnalytics.custom(name, safePayload);
      }
    } catch (_) {
      /* Measurement must never break review rendering. */
    }
  }

  // Loox keeps every upload as name_orig.jpg (full size) and name.jpg (375 x 500).
  // The list shows 64 px, so it asks for the small rendition of the same photo.
  function smallPhoto(url) {
    return /^https:\/\/images\.loox\.io\/.+_orig\.(jpe?g|png|webp)$/i.test(url) ? url.replace(/_orig\.(jpe?g|png|webp)$/i, '.$1') : url;
  }

  function apiBase(root) {
    const storeId = (root.dataset.avReviewPublicStoreId || '').trim();
    if (!storeId) throw new Error('missing_public_store_id');
    return `${API_ORIGIN}/storefront/v1/store/${encodeURIComponent(storeId)}`;
  }

  async function requestJson(url, timeout = 12000) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'omit',
        signal: controller.signal
      });
      if (!response.ok) throw new Error(`http_${response.status}`);
      return await response.json();
    } catch (error) {
      if (error && error.name === 'AbortError') throw new Error('timeout');
      throw error;
    } finally {
      window.clearTimeout(timer);
    }
  }

  async function runLimited(items, concurrency, worker) {
    const results = new Array(items.length);
    let cursor = 0;
    async function next() {
      while (cursor < items.length) {
        const index = cursor;
        cursor += 1;
        results[index] = await worker(items[index], index);
      }
    }
    await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, next));
    return results;
  }

  function listUrl(root, options = {}) {
    const url = new URL(`${apiBase(root)}/product-reviews`);
    url.searchParams.set('sort', 'date');
    url.searchParams.set('direction', 'desc');
    url.searchParams.set('limit', String(options.limit || 200));
    url.searchParams.set('page', String(options.page || 1));
    const locale = root.dataset.avReviewLocale;
    if (locale) url.searchParams.set('locale', locale);
    /* Loox ignores camelCase productId and returns the store-wide feed. */
    if (options.productId) url.searchParams.set('product_id', String(options.productId));
    return url;
  }

  async function fetchVisiblePool(root, context) {
    const surface = root.dataset.avReviewSurface || 'home';
    let ids = unique((context.categoryProducts || []).map((item) => item && item.id));
    const currentId = context.currentProductId ? String(context.currentProductId) : '';
    if (surface === 'product' && currentId) ids = [currentId];
    else if (currentId && !ids.includes(currentId)) ids.unshift(currentId);

    let responses;
    if (surface === 'home') {
      responses = [{ productId: null, data: await requestJson(listUrl(root)) }];
    } else if (ids.length === 0) {
      return { reviews: [], responses: [], ids: [] };
    } else {
      responses = await runLimited(ids, 4, async (productId) => ({
        productId,
        data: await requestJson(listUrl(root, { productId }))
      }));
    }

    const byId = new Map();
    responses.forEach(({ data }) => {
      const reviews = Array.isArray(data && data.reviews) ? data.reviews : [];
      reviews.forEach((review) => {
        if (review && review.id) byId.set(String(review.id), review);
      });
    });

    const reviews = [...byId.values()]
      .sort((left, right) => new Date(right.createdAt || right.date || 0) - new Date(left.createdAt || left.date || 0))
      .slice(0, 200);

    return { reviews, responses, ids };
  }

  async function fetchBottomline(root, pool, context) {
    const surface = root.dataset.avReviewSurface || 'home';
    const currentId = context.currentProductId ? String(context.currentProductId) : '';

    if (surface === 'home') {
      return requestJson(`${apiBase(root)}/product-reviews/bottomline`);
    }

    if (surface === 'product' && currentId) {
      const bottomline = await requestJson(`${apiBase(root)}/product-reviews/bottomline/${encodeURIComponent(currentId)}`);
      const productData = pool.responses[0] && pool.responses[0].data;
      const reviews = Array.isArray(productData && productData.reviews) ? productData.reviews : [];
      const total = safeNumber(productData && productData.pagination && productData.pagination.total, safeNumber(bottomline.totalReviews, reviews.length));
      if (reviews.length >= total) {
        const ratingDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        reviews.forEach((review) => {
          const rating = Math.round(safeNumber(review && review.rating));
          if (rating >= 1 && rating <= 5) ratingDistribution[rating] += 1;
        });
        return { ...bottomline, ratingDistribution };
      }
      return bottomline;
    }

    const complete = pool.responses.every(({ data }) => {
      const reviews = Array.isArray(data && data.reviews) ? data.reviews : [];
      const total = safeNumber(data && data.pagination && data.pagination.total, reviews.length);
      return reviews.length >= total;
    });

    if (complete) {
      const all = pool.responses.flatMap(({ data }) => Array.isArray(data && data.reviews) ? data.reviews : []);
      if (!all.length) return { totalReviews: 0, averageRating: 0 };
      const totalRating = all.reduce((sum, review) => sum + safeNumber(review.rating), 0);
      const ratingDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      all.forEach((review) => {
        const rating = Math.round(safeNumber(review && review.rating));
        if (rating >= 1 && rating <= 5) ratingDistribution[rating] += 1;
      });
      return { totalReviews: all.length, averageRating: totalRating / all.length, ratingDistribution };
    }

    const bottomlines = await runLimited(pool.ids, 4, (productId) => (
      requestJson(`${apiBase(root)}/product-reviews/bottomline/${encodeURIComponent(productId)}`)
    ));
    const totalReviews = bottomlines.reduce((sum, line) => sum + safeNumber(line.totalReviews), 0);
    const weighted = bottomlines.reduce((sum, line) => (
      sum + safeNumber(line.averageRating) * safeNumber(line.totalReviews)
    ), 0);
    const hasCompleteDistribution = bottomlines.every((line) => {
      const distribution = line && line.ratingDistribution;
      return distribution && [1, 2, 3, 4, 5].every((rating) => Number.isFinite(Number(distribution[String(rating)])));
    });
    const ratingDistribution = hasCompleteDistribution
      ? bottomlines.reduce((counts, line) => {
          [1, 2, 3, 4, 5].forEach((rating) => {
            counts[rating] += Math.round(safeNumber(line.ratingDistribution[String(rating)]));
          });
          return counts;
        }, { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 })
      : undefined;
    return {
      totalReviews,
      averageRating: totalReviews > 0 ? weighted / totalReviews : 0,
      ratingDistribution
    };
  }

  function productFromReview(review, context, root) {
    const source = review && review.product ? review.product : {};
    const productId = source.id ? String(source.id) : '';
    // A provider-side association under review is not evidence of another SKU.
    // Keep its original review in the full view, without an unverified label/link.
    if (root.dataset.avReviewSurface === 'home' && root.dataset.avReviewSelection === 'recent') {
      const disputed = String(root.dataset.avReviewDisputedAssociations || '').split(/[\s,]+/).filter(Boolean);
      if (disputed.includes(String(review.id))) return { id: '', handle: '', displayName: '', url: '' };
    }
    const scopedProducts = context.categoryProducts || [];
    const scopedMatch = scopedProducts.find((item) => item && String(item.id) === productId);
    const scoped = scopedMatch || {};
    const currentCatalogIsKnown = scopedProducts.length > 0;
    let handle = scoped.handle ? String(scoped.handle) : '';

    if (!handle && !currentCatalogIsKnown && source.url) {
      try {
        const sourceUrl = new URL(source.url, window.location.origin);
        const parts = sourceUrl.pathname.split('/').filter(Boolean);
        const marker = parts.lastIndexOf('products');
        if (marker >= 0 && parts[marker + 1]) handle = decodeURIComponent(parts[marker + 1]);
      } catch (_) {
        handle = '';
      }
    }

    let url = scoped.url ? String(scoped.url) : '';
    if (!url && handle && (!currentCatalogIsKnown || scopedMatch)) {
      const rootUrl = root.dataset.avReviewRootUrl || '/';
      const prefix = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
      url = `${prefix}products/${encodeURIComponent(handle)}`.replace(/\/{2,}/g, '/');
    }

    return {
      id: productId,
      handle,
      displayName: scoped.displayName ? String(scoped.displayName) : '',
      url
    };
  }

  function normalizeReview(review, context, root) {
    if (!review || !review.id) return null;
    const body = typeof review.body === 'string' ? review.body.trim() : '';
    // Rating-only provider records still belong in the full homepage review pool.
    // They receive no invented quotation or placeholder body.
    if (!body && root.dataset.avReviewSelection !== 'recent') return null;
    const sourceRating = safeNumber(review.rating, 0);
    if (sourceRating < 1 || sourceRating > 5) return null;
    const rating = Math.round(sourceRating);
    const dateValue = review.date || review.createdAt;
    if (root.dataset.avReviewSelection === 'recent' && !dateValue) return null;
    const date = new Date(dateValue || 0);
    if (Number.isNaN(date.getTime())) return null;

    const product = productFromReview(review, context, root);
    const media = Array.isArray(review.media) ? review.media : [];
    const photo = media.find((item) => (
      item && ['photo', 'image'].includes(String(item.type || '').toLowerCase())
      && typeof item.url === 'string' && item.url.startsWith('https://')
    ));
    const highlights = Array.isArray(review.highlights) ? review.highlights : [];
    const title = typeof highlights[0] === 'string' && highlights[0].trim() !== body
      ? highlights[0].trim()
      : '';

    return {
      id: String(review.id),
      rating,
      body,
      title,
      date,
      day: date.toISOString().slice(0, 10),
      verified: review.verified === true,
      reviewerName: review.reviewer && review.reviewer.name ? String(review.reviewer.name) : '',
      productId: product.id,
      productHandle: product.handle,
      productDisplayName: product.displayName,
      productUrl: product.url,
      photoUrl: photo ? smallPhoto(photo.url) : '',
      photoFullUrl: photo ? photo.url : '',
      helpful: Math.max(0, safeNumber(review.helpfulVotes || review.helpfulCount || review.votesHelpful, 0))
    };
  }

  function utility(review) {
    const usefulLength = review.body.length >= 80 && review.body.length <= 400 ? 1 : 0;
    return (review.helpful * 100000) + (usefulLength * 1000) + (review.photoUrl ? 100 : 0) + (review.productHandle ? 10 : 0);
  }

  function selectReviews(rawReviews, context, root) {
    const currentId = context.currentProductId ? String(context.currentProductId) : '';
    const surface = root.dataset.avReviewSurface || 'home';
    const limit = Math.max(1, Math.min(6, safeNumber(root.dataset.avReviewLimit, 6)));
    if (surface === 'home' && root.dataset.avReviewSelection === 'recent') {
      // Chronology is independent of stars, votes, body length and media.
      // Unresolved product associations remain in the full, all-rating view.
      return rawReviews
        .map((review) => normalizeReview(review, context, root))
        .filter((review) => review && review.productId && review.productHandle && review.productUrl && review.productDisplayName)
        .sort((left, right) => right.date - left.date || left.id.localeCompare(right.id))
        .slice(0, Math.min(3, limit));
    }
    const ranked = rawReviews
      .map((review) => normalizeReview(review, context, root))
      .filter(Boolean)
      .sort((left, right) => {
        if (surface === 'product' && currentId) {
          const leftTarget = left.productId === currentId ? 1 : 0;
          const rightTarget = right.productId === currentId ? 1 : 0;
          if (leftTarget !== rightTarget) return rightTarget - leftTarget;
        }
        const scoreDifference = utility(right) - utility(left);
        if (scoreDifference) return scoreDifference;
        return right.date - left.date;
      });

    const chosen = [];
    const productCounts = new Map();
    const dayCounts = new Map();
    const enforceProductDiversity = surface !== 'product';
    const canAdd = (review, products = productCounts, days = dayCounts) => (
      (!enforceProductDiversity || (products.get(review.productId || review.id) || 0) < 2)
      && (days.get(review.day) || 0) < 2
    );
    const add = (review) => {
      chosen.push(review);
      const productKey = review.productId || review.id;
      productCounts.set(productKey, (productCounts.get(productKey) || 0) + 1);
      dayCounts.set(review.day, (dayCounts.get(review.day) || 0) + 1);
    };

    /* Keep the lowest real rating in the unfiltered rail when one exists. */
    const lowerRatings = ranked.filter((review) => review.rating < 5);
    const lowestRating = lowerRatings.reduce((minimum, review) => Math.min(minimum, review.rating), Infinity);
    const requiredLowerRating = lowerRatings.length
      ? ranked.find((review) => review.rating === lowestRating)
      : null;
    if (requiredLowerRating) add(requiredLowerRating);

    for (const review of ranked) {
      if (chosen.length >= limit) break;
      if (chosen.some((selected) => selected.id === review.id)) continue;
      if (canAdd(review)) add(review);
    }

    if (requiredLowerRating && !chosen.some((review) => review.rating < 5)) throw new Error('honesty_rule_unmet');

    const rankIndex = new Map(ranked.map((review, index) => [review.id, index]));
    return chosen.sort((left, right) => rankIndex.get(left.id) - rankIndex.get(right.id));
  }

  function stars(rating) {
    const wrap = make('span', 'av-reviews-editorial__stars');
    wrap.setAttribute('aria-hidden', 'true');
    for (let index = 0; index < 5; index += 1) {
      const base = make('span', 'av-reviews-editorial__star', '☆');
      const fill = make('span', 'av-reviews-editorial__star-fill', '★');
      const amount = Math.max(0, Math.min(1, rating - index));
      fill.style.inlineSize = `${amount * 100}%`;
      base.appendChild(fill);
      wrap.appendChild(base);
    }
    return wrap;
  }

  function formatDate(date, locale) {
    try {
      return new Intl.DateTimeFormat(locale || 'pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'UTC'
      }).format(date);
    } catch (_) {
      return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(date);
    }
  }

  function createCard(review, index, root, instance = 'main') {
    const locale = root.dataset.avReviewLocale || 'pt-BR';
    const card = make('li', 'av-reviews-editorial__card');
    card.dataset.avReviewIndex = String(index);
    card.dataset.avReviewId = review.id;
    if (review.rating < 5) card.dataset.avReviewCritical = 'true';

    const meta = make('div', 'av-reviews-editorial__meta');
    const identity = make('div', 'av-reviews-editorial__identity');
    if (review.reviewerName) identity.appendChild(make('strong', '', review.reviewerName));
    if (review.verified) identity.appendChild(make('span', 'av-reviews-editorial__verified', root.dataset.avCopyVerified));
    const time = make('time', 'av-reviews-editorial__date', formatDate(review.date, locale));
    time.dateTime = review.day;
    meta.append(identity, time);

    const rating = make('div', 'av-reviews-editorial__rating');
    rating.appendChild(stars(review.rating));
    rating.appendChild(make('span', 'visually-hidden', `${review.rating} ${root.dataset.avCopyRatingSuffix}`));

    card.append(meta, rating);
    if (review.title) card.appendChild(make('h3', 'av-reviews-editorial__review-title', review.title));

    const rootId = (root.id || 'reviews').replace(/[^a-zA-Z0-9_-]/g, '');
    const bodyId = `av-review-body-${rootId}-${instance}-${review.id.replace(/[^a-zA-Z0-9_-]/g, '')}`;
    const body = make('p', 'av-reviews-editorial__body', review.body);
    body.id = bodyId;
    if (review.body || root.dataset.avReviewSelection !== 'recent') card.appendChild(body);

    const expand = make('button', 'av-reviews-editorial__expand', root.dataset.avCopyExpand);
    expand.type = 'button';
    expand.hidden = true;
    expand.setAttribute('aria-expanded', 'false');
    expand.setAttribute('aria-controls', bodyId);
    expand.addEventListener('click', () => {
      const isExpanded = expand.getAttribute('aria-expanded') === 'true';
      expand.setAttribute('aria-expanded', String(!isExpanded));
      body.classList.toggle('is-expanded', !isExpanded);
      if (!isExpanded) {
        publish(root, GA_EVENT.expand, {
          index,
          product_handle: review.productHandle || 'unavailable'
        });
      }
    });
    card.appendChild(expand);

    if (review.photoUrl) {
      const figure = make('figure', 'av-reviews-editorial__media');
      const image = make('img');
      if (review.photoFullUrl && review.photoFullUrl !== review.photoUrl) {
        image.addEventListener('error', () => { image.src = review.photoFullUrl; }, { once: true });
      }
      image.src = review.photoUrl;
      image.width = 64;
      image.height = 64;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.alt = (root.dataset.avCopyPhotoAlt || '').replace('__PRODUCT__', review.productDisplayName || 'produto');
      figure.appendChild(image);
      card.appendChild(figure);
    }

    if (review.productUrl && review.productDisplayName && review.productHandle) {
      const productLink = make('a', 'av-reviews-editorial__product-link', review.productDisplayName);
      productLink.href = review.productUrl;
      productLink.addEventListener('click', () => publish(root, GA_EVENT.product, {
        index,
        product_handle: review.productHandle
      }));
      card.appendChild(productLink);
    }

    window.requestAnimationFrame(() => {
      if (body.scrollHeight > body.clientHeight + 1) expand.hidden = false;
    });
    return card;
  }

  function renderAggregate(root, bottomline) {
    const aggregate = root.querySelector('[data-av-reviews-aggregate]');
    const starHost = root.querySelector('[data-av-reviews-aggregate-stars]');
    const textHost = root.querySelector('[data-av-reviews-aggregate-text]');
    const total = safeNumber(bottomline && bottomline.totalReviews, 0);
    const average = safeNumber(bottomline && bottomline.averageRating, 0);
    if (!aggregate || !starHost || !textHost || total <= 0 || average <= 0) return;

    const locale = root.dataset.avReviewLocale || 'pt-BR';
    const ratingText = new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(average);
    const countText = new Intl.NumberFormat(locale).format(total);
    starHost.replaceChildren(stars(average));
    textHost.textContent = (root.dataset.avCopyAggregate || '__AVERAGE__ de 5 · __COUNT__ avaliações')
      .replace('__AVERAGE__', ratingText)
      .replace('__COUNT__', countText);
    aggregate.classList.remove('is-pending');
    aggregate.hidden = false;
    aggregate.removeAttribute('aria-hidden');
  }

  function renderHomeShelfProof(root, bottomline) {
    if (root.dataset.avReviewSurface !== 'home') return;
    const proof = root.ownerDocument.querySelector('[data-au-home-shelf-proof]');
    const textHost = proof && proof.querySelector('[data-au-home-shelf-proof-text]');
    const total = Math.round(safeNumber(bottomline && bottomline.totalReviews, 0));
    const prefix = proof && proof.dataset.auHomeShelfProofPrefix;
    const suffix = proof && proof.dataset.auHomeShelfProofSuffix;
    if (!proof || !textHost || total <= 0 || (!prefix && !suffix)) return;

    const locale = root.dataset.avReviewLocale || 'pt-BR';
    const countText = new Intl.NumberFormat(locale).format(total);
    textHost.textContent = `${prefix || ''}${countText}${suffix || ''}`;
    proof.hidden = false;
    proof.removeAttribute('aria-hidden');
  }

  function renderDistribution(root, bottomline) {
    const distribution = root.querySelector('[data-av-reviews-distribution]');
    const source = bottomline && bottomline.ratingDistribution;
    if (!distribution || !source || typeof source !== 'object') return;

    const locale = root.dataset.avReviewLocale || 'pt-BR';
    const singular = root.dataset.avCopyStarSingular || '';
    const plural = root.dataset.avCopyStarsPlural || '';
    if (!singular || !plural) return;

    const counts = {};
    for (let rating = 1; rating <= 5; rating += 1) {
      const key = String(rating);
      if (!Object.prototype.hasOwnProperty.call(source, key)) return;
      const rawCount = Number(source[key]);
      if (!Number.isFinite(rawCount) || rawCount < 0) return;
      counts[key] = Math.round(rawCount);
    }

    const sourcedTotal = Math.round(safeNumber(bottomline && bottomline.totalReviews, 0));
    const distributionTotal = Object.values(counts).reduce((sum, count) => sum + count, 0);
    if (sourcedTotal <= 0 || distributionTotal !== sourcedTotal) return;

    const rows = [];

    for (let rating = 5; rating >= 1; rating -= 1) {
      const count = counts[String(rating)];
      const term = rating === 1 ? singular : plural;
      const item = make('div', 'av-reviews-editorial__distribution-row');
      const label = make('dt', '', `${rating} ${term}`);
      const value = make('dd', '', new Intl.NumberFormat(locale).format(count));
      item.append(label, value);
      rows.push(item);
    }

    distribution.replaceChildren(...rows);
    distribution.hidden = false;
  }

  function observeImpression(root, cardCount) {
    let timer = 0;
    let fired = false;
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5);
      if (visible && !fired && !timer) {
        timer = window.setTimeout(() => {
          fired = true;
          timer = 0;
          publish(root, GA_EVENT.view, { card_count: cardCount });
          observer.disconnect();
        }, 1000);
      } else if (!visible && timer) {
        window.clearTimeout(timer);
        timer = 0;
      }
    }, { threshold: [0.5] });
    observer.observe(root);
  }

  function attachRailInteractions(root, list) {
    const prev = root.querySelector('[data-av-reviews-prev]');
    const next = root.querySelector('[data-av-reviews-next]');
    const controls = root.querySelector('[data-av-reviews-controls]');
    let lastIndex = 0;
    let method = 'swipe';
    let scrollTimer = 0;

    const step = () => {
      const card = list.querySelector('.av-reviews-editorial__card');
      if (!card) return list.clientWidth * 0.86;
      const styles = window.getComputedStyle(list);
      return card.getBoundingClientRect().width + safeNumber(parseFloat(styles.columnGap || styles.gap), 0);
    };
    const current = () => Math.max(0, Math.round(list.scrollLeft / Math.max(1, step())));
    const emit = () => {
      const index = current();
      if (index === lastIndex) return;
      lastIndex = index;
      publish(root, GA_EVENT.advance, { method, index });
    };
    const updateControls = () => {
      if (!controls || !prev || !next) return;
      const overflows = list.scrollWidth > list.clientWidth + 2;
      controls.hidden = !overflows;
      prev.disabled = !overflows || list.scrollLeft <= 2;
      next.disabled = !overflows || list.scrollLeft >= list.scrollWidth - list.clientWidth - 2;
    };
    const scrollByCard = (direction, interactionMethod) => {
      method = interactionMethod;
      list.scrollBy({
        left: direction * step(),
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
      window.setTimeout(emit, reducedMotion.matches ? 0 : 350);
    };

    list.addEventListener('pointerdown', () => { method = 'swipe'; }, { passive: true });
    list.addEventListener('touchstart', () => { method = 'swipe'; }, { passive: true });
    list.addEventListener('wheel', () => { method = 'wheel'; }, { passive: true });
    list.addEventListener('scroll', () => {
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => {
        emit();
        updateControls();
      }, 160);
    }, { passive: true });
    list.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (list.scrollWidth <= list.clientWidth + 2) return;
      event.preventDefault();
      scrollByCard(event.key === 'ArrowRight' ? 1 : -1, 'keyboard');
    });
    if (prev) prev.addEventListener('click', () => scrollByCard(-1, 'button'));
    if (next) next.addEventListener('click', () => scrollByCard(1, 'button'));
    window.addEventListener('resize', updateControls, { passive: true });
    root.addEventListener('av:reviews-revealed', updateControls);
    updateControls();
  }

  async function fetchReviewPages(root, productId = '') {
    const reviews = new Map();
    let page = 1;
    let hasMore = true;
    while (hasMore && page <= 100) {
      const sizeBeforePage = reviews.size;
      const data = await requestJson(listUrl(root, { limit: 200, page, productId }), 15000);
      (Array.isArray(data && data.reviews) ? data.reviews : []).forEach((review) => {
        if (review && review.id) reviews.set(String(review.id), review);
      });
      hasMore = Boolean(data && data.pagination && data.pagination.hasMore);
      if (hasMore && reviews.size === sizeBeforePage) throw new Error('pagination_stalled');
      page += 1;
    }
    if (hasMore) throw new Error('pagination_limit');
    return [...reviews.values()];
  }

  async function fetchAllReviews(root, context) {
    const surface = root.dataset.avReviewSurface || 'home';
    const currentId = context.currentProductId ? String(context.currentProductId) : '';
    if (surface === 'product' && currentId) return fetchReviewPages(root, currentId);

    const ids = unique((context.categoryProducts || []).map((item) => item && item.id));
    if (surface !== 'collection' || ids.length === 0) return fetchReviewPages(root);

    const groups = await runLimited(ids, 3, (productId) => fetchReviewPages(root, productId));
    const byId = new Map();
    groups.flat().forEach((review) => {
      if (review && review.id) byId.set(String(review.id), review);
    });
    return [...byId.values()]
      .sort((left, right) => new Date(right.createdAt || right.date || 0) - new Date(left.createdAt || left.date || 0));
  }

  function attachAllReviews(root, context) {
    const link = root.querySelector('[data-av-reviews-all-link]');
    const dialog = root.querySelector('[data-av-reviews-dialog]');
    const close = root.querySelector('[data-av-reviews-dialog-close]');
    const list = root.querySelector('[data-av-reviews-dialog-list]');
    const productFilter = root.querySelector('[data-av-reviews-filter-product]');
    const ratingFilter = root.querySelector('[data-av-reviews-filter-rating]');
    const loadMore = root.querySelector('[data-av-reviews-load-more]');
    const status = root.querySelector('[data-av-reviews-dialog-status]');
    if (!link || !dialog || !close || !list || !productFilter || !ratingFilter || !loadMore) return;
    if (root.dataset.avReviewSelection === 'recent') link.hidden = false;

    let allReviews = [];
    let visible = 24;
    let loadingPromise;

    const render = (reset = false) => {
      if (reset) visible = 24;
      const productId = productFilter.value;
      const rating = ratingFilter.value;
      const filtered = allReviews.filter((review) => (
        (!productId || review.productId === productId)
        && (!rating || String(review.rating) === rating)
      ));
      const shown = filtered.slice(0, visible);
      list.replaceChildren(...shown.map((review, index) => createCard(review, index, root, 'all')));
      loadMore.hidden = shown.length >= filtered.length;
      if (status) status.textContent = (root.dataset.avCopyShowing || '').replace('__COUNT__', String(filtered.length));
    };

    const populateProducts = () => {
      const products = new Map();
      allReviews.forEach((review) => {
        if (review.productId && review.productDisplayName) products.set(review.productId, review.productDisplayName);
      });
      [...products.entries()]
        .sort((left, right) => left[1].localeCompare(right[1], root.dataset.avReviewLocale || 'pt-BR'))
        .forEach(([id, name]) => {
          const option = make('option', '', name);
          option.value = id;
          productFilter.appendChild(option);
        });
    };

    const ensureLoaded = () => {
      if (loadingPromise) return loadingPromise;
      list.replaceChildren(make('li', 'av-reviews-editorial__dialog-loading', root.dataset.avCopyLoading));
      loadingPromise = fetchAllReviews(root, context)
        .then((raw) => {
          allReviews = raw.map((review) => normalizeReview(review, context, root)).filter(Boolean);
          populateProducts();
          render(true);
        })
        .catch((error) => {
          list.replaceChildren(make('li', 'av-reviews-editorial__dialog-error', root.dataset.avCopyError));
          publish(root, GA_EVENT.error, { reason: `all_${String(error && error.message || 'unknown').slice(0, 40)}` });
        });
      return loadingPromise;
    };

    link.addEventListener('click', (event) => {
      event.preventDefault();
      publish(root, GA_EVENT.all);
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      ensureLoaded();
    });
    close.addEventListener('click', () => {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      link.focus();
    });
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      link.focus();
    });
    dialog.addEventListener('close', () => link.focus());
    productFilter.addEventListener('change', () => render(true));
    ratingFilter.addEventListener('change', () => render(true));
    loadMore.addEventListener('click', () => {
      visible += 24;
      render();
    });
  }

  function setFailure(root, reason) {
    if (root.classList.contains('au-home-master-reviews')) root.dataset.avReviewState = 'error';
    const list = root.querySelector('[data-av-reviews-list]');
    const stateNode = root.querySelector('[data-av-reviews-state]');
    if (list) {
      list.setAttribute('aria-busy', 'false');
      list.hidden = true;
    }
    if (stateNode) {
      stateNode.textContent = root.dataset.avCopyError;
      stateNode.hidden = false;
    }
    publish(root, GA_EVENT.error, { reason: String(reason || 'unknown').slice(0, 40) });
  }

  async function load(root) {
    const context = parseContext(root);
    const list = root.querySelector('[data-av-reviews-list]');
    const stateNode = root.querySelector('[data-av-reviews-state]');
    const support = root.querySelector('[data-av-reviews-support]');
    const policy = root.querySelector('[data-av-reviews-policy]');
    if (!list || !stateNode || !support) return;

    try {
      const pool = await fetchVisiblePool(root, context);
      const selected = selectReviews(pool.reviews, context, root);
      list.setAttribute('aria-busy', 'false');

      if (!selected.length) {
        if (root.classList.contains('au-home-master-reviews')) root.dataset.avReviewState = 'empty';
        list.hidden = true;
        stateNode.textContent = root.dataset.avCopyEmpty;
        stateNode.hidden = false;
        return;
      }

      list.replaceChildren(...selected.map((review, index) => createCard(review, index, root)));
      if (root.classList.contains('au-home-master-reviews')) root.dataset.avReviewState = 'ready';
      list.hidden = false;
      if (root.dataset.avReviewSelection !== 'recent') {
        support.textContent = selected.every((review) => review.verified)
          ? root.dataset.avCopySupportVerified
          : root.dataset.avCopySupportStore;
      }
      support.classList.remove('is-pending');
      support.hidden = false;
      support.removeAttribute('aria-hidden');
      const policyCopy = (root.dataset.avCopyPolicy || '').trim();
      if (policy && policyCopy && !policyCopy.toLowerCase().includes('translation missing')) {
        policy.textContent = policyCopy;
        policy.hidden = false;
      }
      attachRailInteractions(root, list);
      attachAllReviews(root, context);
      observeImpression(root, selected.length);

      try {
        const bottomline = await fetchBottomline(root, pool, context);
        renderAggregate(root, bottomline);
        renderDistribution(root, bottomline);
        renderHomeShelfProof(root, bottomline);
      } catch (_) {
        /* The aggregate line fails closed while the sourced review cards remain useful. */
      }
      // Release 4, P1.10: served hidden; shown only now that cards exist, and never while its place is on screen.
      if (root.hasAttribute('data-av-reviews-reveal') && list.children.length) reveal(root);
    } catch (error) {
      setFailure(root, error && error.message);
    }
  }

  function reveal(root) {
    if (!root.hidden) return;
    const anchor = root.parentElement || root;
    const show = () => {
      // Hold the view: opening above the viewport, scroll by what the wrapper grew,
      // less what native scroll anchoring already moved. Below the viewport nothing visible moves.
      const above = anchor.getBoundingClientRect().bottom <= 0;
      const y0 = window.scrollY;
      const h0 = anchor.getBoundingClientRect().height;
      root.hidden = false;
      if (above) {
        const grew = anchor.getBoundingClientRect().height - h0;
        const rest = grew - (window.scrollY - y0);
        if (Math.abs(rest) > 0.5) window.scrollBy({ top: rest, behavior: 'instant' });
      }
      root.dispatchEvent(new CustomEvent('av:reviews-revealed'));
    };
    const box = anchor.getBoundingClientRect();
    if (!('IntersectionObserver' in window) || box.top >= window.innerHeight || box.bottom <= 0) {
      show();
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => !entry.isIntersecting)) {
        observer.disconnect();
        show();
      }
    });
    observer.observe(anchor);
  }

  function init(root) {
    if (!root || root.dataset.avReviewsInitialized === 'true') return;
    root.dataset.avReviewsInitialized = 'true';
    if (!('IntersectionObserver' in window)) {
      load(root);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      load(root);
    }, { rootMargin: '600px 0px' });
    // A hidden section has no box; its section wrapper still marks the place.
    observer.observe(root.hidden ? (root.parentElement || root) : root);
  }

  function scan(scope = document) {
    if (scope.matches && scope.matches(ROOT_SELECTOR)) init(scope);
    if (scope.querySelectorAll) scope.querySelectorAll(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  } else {
    scan();
  }
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
